import { Layout } from "@/components/layout/layout";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useConfirmMyEmailChange, getGetPlayerMeQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle, Mail } from "lucide-react";

function getTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
}

export default function ConfirmEmail() {
  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [newEmail, setNewEmail] = useState<string>("");
  const queryClient = useQueryClient();
  const confirm = useConfirmMyEmailChange();

  useEffect(() => {
    const token = getTokenFromUrl();
    if (!token) {
      setStatus("error");
      setErrorMsg("Missing confirmation token.");
      return;
    }
    confirm.mutate(
      { data: { token } },
      {
        onSuccess: (updated) => {
          setNewEmail(updated.email);
          setStatus("success");
          // Server clears the session cookie, so /me cache is stale. Drop it.
          queryClient.removeQueries({ queryKey: getGetPlayerMeQueryKey() });
        },
        onError: (err: Error) => {
          let msg = "This confirmation link is invalid or has expired.";
          try {
            const parsed = JSON.parse(err.message) as { error?: string };
            if (parsed?.error) msg = parsed.error;
          } catch {
            // not JSON
          }
          setErrorMsg(msg);
          setStatus("error");
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-xl">
        <Card className="bg-background border-border shadow-md">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" /> Confirm Email Change
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {status === "pending" ? (
              <div className="flex items-center gap-3 text-muted-foreground" data-testid="confirm-pending">
                <Loader2 className="h-5 w-5 animate-spin" /> Confirming your new email…
              </div>
            ) : null}
            {status === "success" ? (
              <div className="space-y-4" data-testid="confirm-success">
                <div className="flex items-center gap-3 text-green-500">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Email updated</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your account email is now <strong>{newEmail}</strong>. For your security
                  you've been signed out everywhere — sign back in with your new email.
                </p>
                <Link href="/login">
                  <Button className="bg-primary text-primary-foreground">Go to sign in</Button>
                </Link>
              </div>
            ) : null}
            {status === "error" ? (
              <div className="space-y-4" data-testid="confirm-error">
                <div className="flex items-center gap-3 text-destructive">
                  <XCircle className="h-5 w-5" />
                  <span className="font-medium">Couldn't confirm</span>
                </div>
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
                <Link href="/me">
                  <Button variant="outline">Back to your profile</Button>
                </Link>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
